import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Active-school context (PRO-0030 §5). Holds the caller's chosen active school
 * and persists it to `localStorage` (DS-5), validated against the schools the
 * caller is actually assigned to on load (a stale/foreign stored id falls back
 * to the only/first school — the server re-validates regardless, so this is UX,
 * not a trust boundary).
 */

const STORAGE_KEY = 'ucp.activeSchoolId';

export interface SchoolContextValue {
  readonly activeSchoolId: string | null;
  readonly setActiveSchoolId: (schoolId: string) => void;
}

const SchoolContext = createContext<SchoolContextValue | undefined>(undefined);

function readStored(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(schoolId: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, schoolId);
  } catch {
    /* storage unavailable (private mode); choice is in-memory only */
  }
}

export function resolveInitialSchoolId(availableSchoolIds: readonly string[]): string | null {
  if (availableSchoolIds.length === 0) {
    return null;
  }
  const stored = readStored();
  if (stored !== null && availableSchoolIds.includes(stored)) {
    return stored;
  }
  return availableSchoolIds[0] ?? null;
}

export function SchoolContextProvider({
  availableSchoolIds,
  children,
}: {
  availableSchoolIds: readonly string[];
  children: ReactNode;
}) {
  const [activeSchoolId, setActiveState] = useState<string | null>(() =>
    resolveInitialSchoolId(availableSchoolIds),
  );

  const setActiveSchoolId = useCallback((schoolId: string) => {
    setActiveState(schoolId);
    writeStored(schoolId);
  }, []);

  const value = useMemo<SchoolContextValue>(
    () => ({ activeSchoolId, setActiveSchoolId }),
    [activeSchoolId, setActiveSchoolId],
  );

  return <SchoolContext.Provider value={value}>{children}</SchoolContext.Provider>;
}

export function useSchoolContext(): SchoolContextValue {
  const context = useContext(SchoolContext);
  if (!context) {
    throw new Error('useSchoolContext must be used within a SchoolContextProvider');
  }
  return context;
}
